/**
 * Client-side agent bridge — calls the Horsir server /api/agent endpoint.
 * Owns AgentResult/AgentTraceItem types used by the taskpane UI.
 */

import { ChatMessage, ContextConfig, HostKind, LLMConfig } from "./types";
import { SearchConfig } from "./settings";

export interface AgentTraceItem {
  tool: string;
  args?: Record<string, unknown>;
  ok?: boolean;
  /** true when this call is awaiting client-side execution. */
  pending?: boolean;
  summary?: string;
}
export interface AgentPendingCall {
  id: string;
  name: string;
  args: Record<string, unknown>;
}
export interface AgentResult {
  /** Final assistant text (empty while done === false). */
  content: string;
  /** Client tool calls awaiting execution; non-empty when done === false. */
  pending: AgentPendingCall[];
  trace: AgentTraceItem[];
  /** Full conversation including this step's assistant + tool messages. */
  messages: ChatMessage[];
  /** true once a final answer is produced. */
  done: boolean;
}

/** Is a Horsir server (with the agent API) reachable at the same origin? */
export async function agentHealth(): Promise<{ ok: boolean; serverTools: string[]; clientTools: string[] }> {
  try {
    const resp = await fetch("/api/health", { method: "GET" });
    if (!resp.ok) return { ok: false, serverTools: [], clientTools: [] };
    const data = await resp.json();
    return {
      ok: Boolean(data?.ok),
      serverTools: Array.isArray(data?.serverTools) ? data.serverTools : [],
      clientTools: Array.isArray(data?.clientTools) ? data.clientTools : [],
    };
  } catch {
    return { ok: false, serverTools: [], clientTools: [] };
  }
}

/** Advance the agent one round. Caller owns `messages` and resumes by appending
 *  { role:"tool", tool_call_id, content } for each pending call, then calling again. */
export async function runAgent(payload: {
  llm: LLMConfig;
  search: SearchConfig;
  host: HostKind;
  webSearch: boolean;
  messages: ChatMessage[];
  signal?: AbortSignal;
  contextConfig?: ContextConfig;
}): Promise<AgentResult> {
  const resp = await fetch("/api/agent", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal: payload.signal,
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data?.error || `服务端错误 ${resp.status}`);
  return {
    content: data?.content ?? "",
    pending: Array.isArray(data?.pending) ? data.pending : [],
    trace: Array.isArray(data?.trace) ? data.trace : [],
    messages: Array.isArray(data?.messages) ? data.messages : [],
    done: Boolean(data?.done),
  };
}
